/**
 * Showing scheduling date/time helpers — shared between the request
 * form and the My Showings reschedule flow so the gating rules are
 * never duplicated.
 *
 *   - MIN_NOTICE_HOURS (24): showings must be at least 24h from now.
 *   - CREDIT_FORFEITURE_HOURS (4): cancelling or rescheduling within
 *     this window forfeits the borrower's showing credit.
 *
 * Backend mirrors the 24h rule in routes/showami.js validateShowingTime
 * and the 4h rule in the cancel/reschedule endpoints so the floor is
 * defended on both sides.
 */
export const MIN_NOTICE_HOURS = 24;
export const CREDIT_FORFEITURE_HOURS = 4;

const HOUR_MS = 60 * 60 * 1000;

export const getMinShowingDatetime = () =>
  new Date(Date.now() + MIN_NOTICE_HOURS * HOUR_MS);

/**
 * Combine a date string (YYYY-MM-DD) or Date with a time string. The
 * time string can be either 24-hour ("14:30") or 12-hour with period
 * ("2:30 PM") — the form's TIME_SLOTS use 24h; My Showings rows
 * sometimes carry pre-formatted display strings, so we accept both.
 */
export const combineDateAndTime = (dateOrStr, timeString) => {
  if (!dateOrStr) return null;
  // 🚨 THE DATE HALF FABRICATED TOO, AND WORSE THAN THE TIME HALF DID. It was
  // `new Date(yy || 1970, (mm || 1) - 1, dd || 1)` — so "next Tuesday" split to
  // NaN and returned 1 JANUARY 1970, a valid Date comfortably outside every
  // window, and "2026-02-30" silently rolled over to 2 March. Both were then
  // measured against the credit-forfeiture rule as though they were real.
  //
  // ⚠️ THE ROUND-TRIP COMPARISON IS WHAT CATCHES THE ROLLOVER. A constructed
  // Date is always valid; the only way to tell "parsed" from "coerced" is to
  // read the components back. Mirrors routes/showami.js showingAbsoluteFromRow,
  // and test/showingForfeiture.test.js asserts the two agree input by input.
  let base;
  if (typeof dateOrStr === 'string') {
    const dm = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(dateOrStr.trim());
    if (!dm) return null;
    const [yy, mm, dd] = [Number(dm[1]), Number(dm[2]), Number(dm[3])];
    base = new Date(yy, mm - 1, dd);
    if (base.getFullYear() !== yy || base.getMonth() !== mm - 1 || base.getDate() !== dd) return null;
  } else {
    base = new Date(dateOrStr);
    if (isNaN(base.getTime())) return null;
  }
  const t = String(timeString || '').trim();
  const ampm = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(t);
  const h24  = /^(\d{1,2}):(\d{2})$/.exec(t);
  let hours = 0;
  let minutes = 0;
  if (ampm) {
    hours = Number(ampm[1]);
    minutes = Number(ampm[2]);
    const period = ampm[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
  } else if (h24) {
    hours = Number(h24[1]);
    minutes = Number(h24[2]);
  } else {
    // 🚨 NO TIME MEANS NO MOMENT, NOT MIDNIGHT. This fell through with
    // hours = 0 and returned 00:00 on the requested date — a fabricated
    // instant, and the value isWithin4Hours then measured the credit-forfeiture
    // window against.
    //
    // 🔑 IT WAS REACHABLE: the Homes "Submit a property" form stores a PERIOD
    // in preferred_time — morning / afternoon / evening / flexible — and none of
    // those match either regex above. The server's twin
    // (routes/showami.js showingAbsoluteFromRow) fabricated NOON for the same
    // value, so one showing carried two invented timestamps twelve hours apart
    // and the browser and the server could disagree about whether the borrower
    // was about to lose their credit.
    return null;
  }
  if (hours > 23 || minutes > 59) return null;
  base.setHours(hours, minutes, 0, 0);
  return base;
};

/**
 * Whether an entire calendar date is too soon — true when the latest
 * second of that date is still before the 24h floor. Use this on the
 * date picker's `min` attribute and to gray out date pills.
 */
export const isDateDisabled = (dateOrStr) => {
  if (!dateOrStr) return false;
  const end = typeof dateOrStr === 'string'
    ? (() => {
        const [yy, mm, dd] = dateOrStr.split('-').map(Number);
        return new Date(yy || 1970, (mm || 1) - 1, dd || 1, 23, 59, 59, 999);
      })()
    : (() => { const d = new Date(dateOrStr); d.setHours(23, 59, 59, 999); return d; })();
  return end < getMinShowingDatetime();
};

/**
 * Whether a specific time slot on a given date violates the 24h
 * minimum. Used to filter the dropdown so the borrower can't pick a
 * slot that the backend would reject anyway.
 */
export const isTimeSlotDisabled = (dateOrStr, timeString) => {
  const slot = combineDateAndTime(dateOrStr, timeString);
  if (!slot || isNaN(slot.getTime())) return true;
  return slot < getMinShowingDatetime();
};

/**
 * Earliest YYYY-MM-DD a showing can be scheduled for, given the 24h
 * floor. Always at least tomorrow (and the day after when 24h-from-now
 * actually falls into the day after tomorrow).
 */
export const earliestValidDateStr = () => {
  const d = getMinShowingDatetime();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * 7 days from today as YYYY-MM-DD — matches Showami's 7-day max
 * scheduling window.
 */
export const maxValidDateStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Every valid quarter-hour slot in Doorly's showing window, as
 * { value: "14:30", label: "2:30 PM" }.
 *
 * 🚨 THIS LIVED INSIDE RequestShowingModal.jsx AND HAD NO COUNTERPART. The Homes
 * "Submit a property" form offered a <select> of PERIODS instead — morning /
 * afternoon / evening / flexible — which went into the same TEXT column this
 * grid's values go into, and which every reader then tried to parse as a clock
 * time. Two controls for one field is how that happened; moving the grid here is
 * what stops the next one.
 *
 * ⚠️ THE WINDOW AND THE GRANULARITY MIRROR backend/utils/showingTime.js. Showami
 * requires quarter-hour minutes; 8:00 AM–8:45 PM is Doorly's. There is no import
 * path between frontend/ and backend/, so this is a checked copy —
 * test/showingForfeiture.test.js compares the two.
 */
export const generateTimeSlots = () => {
  const slots = [];
  for (let h = 8; h <= 20; h++) {
    for (const m of [0, 15, 30, 45]) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? 'AM' : 'PM';
      slots.push({ value, label: `${hour12}:${String(m).padStart(2, '0')} ${ampm}` });
    }
  }
  return slots;
};

export const TIME_SLOTS = generateTimeSlots();

/**
 * Whether the given showing is within the 4-hour credit-forfeiture
 * window. Accepts either a combined datetime, or { preferred_date,
 * preferred_time } as stored on the showing_requests row.
 */
export const isWithin4Hours = (showingOrDatetime) => {
  if (!showingOrDatetime) return false;
  let when;
  if (showingOrDatetime instanceof Date) {
    when = showingOrDatetime;
  } else if (typeof showingOrDatetime === 'string') {
    when = new Date(showingOrDatetime);
  } else if (showingOrDatetime?.showing_at) {
    // 🔑 THE STORED INSTANT WINS. It was written where the borrower's timezone
    // was known; rebuilding from the two strings below can only approximate it,
    // and the server's cancel endpoint now reads the instant — so preferring the
    // reconstruction here would let the warning and the charge disagree.
    when = new Date(showingOrDatetime.showing_at);
  } else if (showingOrDatetime?.preferred_period) {
    // ⚠️ A PERIOD IS NOT AN INSTANT. No moment, so no forfeiture warning —
    // matching routes/showami.js showingAbsoluteFromRow exactly.
    return false;
  } else if (showingOrDatetime?.preferred_date) {
    // ⚠️ NO `|| '12:00'`. That default invented noon for a row with no stored
    // time and then measured the forfeiture window against it. A showing whose
    // moment cannot be established falls through to the `!when` guard below and
    // returns false — no warning, no forfeiture — which matches what the server
    // does with the same row.
    when = combineDateAndTime(
      showingOrDatetime.preferred_date,
      showingOrDatetime.preferred_time
    );
  }
  if (!when || isNaN(when.getTime())) return false;
  const diffMs = when.getTime() - Date.now();
  return diffMs > 0 && diffMs < CREDIT_FORFEITURE_HOURS * HOUR_MS;
};
